import { Component, ElementRef, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { MessageService } from 'primeng/api'
import { formatDate } from '@angular/common'
import { catchError, map, of } from 'rxjs'

import {
  CatalogEntry,
  DocumentEntityType,
  DocumentFile,
  DocumentsCatalog,
  DocsEntity,
  GuarantorDocs,
  HolderType,
  RefreshInterval,
  ReviewStatus,
  SectionMeta,
} from '@data/interfaces/documents.interface'
import { conExtensionDe, guardarBlobComo, nombreLegibleDeUrl } from '@data/utils/descarga-archivo.util'
import { ApiDocumentsService } from '@data/services/modules/documents/api-documents.service'
import { AuthService } from '@data/services/modules/auth/auth.service'
import { environment } from '@env/environment'

const EMPTY_FILE: DocumentFile = { url: '', uploadedAt: null, refreshInterval: null }
const INTERVAL_MONTHS: Record<string, number> = { '1m': 1, '3m': 3, '1y': 12 }
const MAX_FILE_BYTES = 100 * 1024 * 1024

@Component({
  selector: 'app-customer-documents',
  templateUrl: './customer-documents.component.html',
  styleUrls: ['./customer-documents.component.scss'],
  providers: [MessageService],
})
export class CustomerDocumentsComponent implements OnChanges {
  /** Modo cliente: cliente con `docs` populado (holder+guarantors), `rfc` y `documentType`. */
  @Input() client: any
  /** Modo lease (o explícito): entidad de docs + tipo + RFC directos. */
  @Input() docsEntity: any
  @Input() entityType: DocumentEntityType | undefined
  @Input() ownerRfc: string | undefined
  /**
   * `client`: el expediente oficial, editable y con revisión. `lease-readonly`: la copia que vive en un
   * arrendamiento, solo lectura total (la API responde 403 a toda escritura sobre ella, Admin incluido).
   * El valor por defecto es el seguro: quien olvide declararlo obtiene una vista que no escribe.
   */
  @Input() mode: 'client' | 'lease-readonly' = 'lease-readonly'
  @Output() changed = new EventEmitter<void>()

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>

  catalog: DocumentsCatalog | null = null
  sections: SectionMeta[] = []
  docs: DocsEntity | null = null
  documentType: DocumentEntityType = 'NaturalEntityDocs'
  rfc = ''
  docsId = ''
  activeTab = 'holder' // 'holder' | <guarantorId>
  canEditInterval = false
  canApprove = false

  /** Secciones expandidas (por key). Vacío = todas colapsadas (estado inicial). */
  expandedSections = new Set<string>()
  /** Circunferencia del anillo de progreso por sección (r=9). */
  readonly ringC = 2 * Math.PI * 9

  uploadingFields = new Set<string>()
  multiAccept = false
  private pendingUpload: { field: string; isArray: boolean } | null = null
  // Archivos en espera de elegir vigencia antes de subir (staff). Para single es 1
  // archivo; para array es el lote completo (todos heredan la vigencia elegida).
  private pendingFiles: File[] | null = null
  private pendingField = ''
  private pendingIsArray = false
  private pendingOldUrl: string | undefined = undefined

  intervalOptions = [
    { label: 'No requiere', value: null },
    { label: 'Cada 1 mes', value: '1m' },
    { label: 'Cada 3 meses', value: '3m' },
    { label: 'Cada 1 año', value: '1y' },
  ]

  // Visor
  previewVisible = false
  previewUrl: string | null = null
  previewName: string | null = null
  /** Archivo abierto en el visor: su botón de descarga tiene que bajar ESTE, con su nombre. */
  previewFile: DocumentFile | null = null

  // Prompt de intervalo tras subir
  showIntervalPrompt = false
  intervalPromptField = ''

  // Modal de confirmación (estilo plataforma)
  showConfirm = false
  confirmMessage = ''
  private confirmAction: (() => void) | null = null

  // Revisión (Por Aprobar): rechazo con motivo obligatorio
  showRejectDialog = false
  rejectReason = ''
  private reviewTarget: { field: string; index: number; url: string } | null = null
  reviewBusy = false

  // Renombrar archivo (solo staff, documents.update)
  showRenameDialog = false
  renameValue = ''
  private renameTarget: { field: string; index: number; url: string } | null = null

  // Modal agregar aval
  showAddGuarantor = false
  newGuarantorType: HolderType = 'PF'
  newGuarantorName = ''
  guarantorTypeOptions = [
    { label: 'Persona Física', value: 'PF' },
    { label: 'Persona Moral', value: 'PM' },
  ]

  saving = false

  constructor(
    private api: ApiDocumentsService,
    private auth: AuthService,
    private http: HttpClient,
    private messageService: MessageService,
    @Inject(LOCALE_ID) private locale: string,
  ) {
    this.canEditInterval = this.auth.userHasAuthorization('documents', 'update')
    this.canApprove = this.auth.userHasAuthorization('documents', 'approve')
  }

  /** Expediente del cliente: el único donde se escribe. Toda acción de escritura y de revisión vive aquí. */
  get isClientContext(): boolean {
    return this.mode === 'client'
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['client'] || changes['docsEntity'] || changes['entityType']) && (this.client || this.docsEntity)) this.hydrate()
  }

  // ── carga ────────────────────────────────────────────────────────────────
  private hydrate(): void {
    this.documentType = (this.entityType ?? this.client?.documentType) as DocumentEntityType
    this.rfc = this.ownerRfc ?? this.client?.rfc ?? ''
    const d = this.docsEntity ?? this.client?.docs
    if (d && typeof d === 'object') {
      this.docs = { _id: d._id, holder: d.holder || {}, guarantors: d.guarantors || [], totalDocuments: d.totalDocuments, uploadedDocuments: d.uploadedDocuments }
      this.docsId = d._id
    } else {
      this.docs = null
      this.docsId = typeof d === 'string' ? d : ''
    }
    if (this.activeTab !== 'holder' && !this.docs?.guarantors.find((g) => g._id === this.activeTab)) this.activeTab = 'holder'
    if (!this.catalog) this.loadCatalog()
    else this.autoExpandPendingSections()
  }

  private loadCatalog(): void {
    this.api.getCatalog().subscribe((res) => {
      if (res?.catalog) this.catalog = res.catalog
      this.sections = res?.sections || []
      this.autoExpandPendingSections()
    })
  }

  /** Auto-expande las secciones del tab activo que tengan archivos por aprobar. */
  private autoExpandPendingSections(): void {
    if (!this.catalog || !this.docs) return
    for (const sec of this.groupedEntries) if (this.sectionStats(sec.entries).pendingCount > 0) this.expandedSections.add(sec.key)
  }

  private notifyChanged(): void {
    this.changed.emit()
  }

  // ── navegación de sub-tabs ─────────────────────────────────────────────────
  get holderType(): HolderType {
    return this.documentType === 'LegalEntityDocs' ? 'PM' : 'PF'
  }
  get guarantors(): GuarantorDocs[] {
    return this.docs?.guarantors || []
  }
  get activeGuarantor(): GuarantorDocs | null {
    return this.activeTab === 'holder' ? null : this.guarantors.find((g) => g._id === this.activeTab) || null
  }
  get currentRole(): 'holder' | 'guarantor' {
    return this.activeTab === 'holder' ? 'holder' : 'guarantor'
  }
  get currentType(): HolderType {
    return this.activeTab === 'holder' ? this.holderType : this.activeGuarantor?.type || 'PF'
  }
  get currentEntries(): CatalogEntry[] {
    return this.catalog ? this.catalog[this.currentRole][this.currentType] || [] : []
  }
  // Cache para no re-allocar en cada ciclo de change detection (rompería el *ngFor).
  private groupedCache: { key: string; value: { key: string; label: string; entries: CatalogEntry[] }[] } | null = null

  /** Entradas del tab activo agrupadas por sección, en el orden del catálogo de secciones. */
  get groupedEntries(): { key: string; label: string; entries: CatalogEntry[] }[] {
    const cacheKey = `${this.currentRole}:${this.currentType}:${this.catalog ? '1' : '0'}:${this.sections.length}`
    if (this.groupedCache?.key === cacheKey) return this.groupedCache.value
    const entries = this.currentEntries
    const order = this.sections.length ? this.sections : this.fallbackSections(entries)
    const byKey = new Map<string, CatalogEntry[]>()
    for (const e of entries) {
      const arr = byKey.get(e.section) || []
      arr.push(e)
      byKey.set(e.section, arr)
    }
    const value = order.filter((s) => byKey.has(s.key)).map((s) => ({ key: s.key, label: s.label, entries: byKey.get(s.key)! }))
    this.groupedCache = { key: cacheKey, value }
    return value
  }
  /** Si el backend no mandó `sections`, derivar el orden de aparición (degradado seguro). */
  private fallbackSections(entries: CatalogEntry[]): SectionMeta[] {
    const seen: SectionMeta[] = []
    for (const e of entries) if (!seen.some((s) => s.key === e.section)) seen.push({ key: e.section, label: e.section })
    return seen
  }
  private get container(): Record<string, DocumentFile | DocumentFile[]> | null {
    if (!this.docs) return null
    return this.activeTab === 'holder' ? this.docs.holder : this.activeGuarantor?.docs || null
  }
  selectTab(id: string): void {
    this.activeTab = id
    this.autoExpandPendingSections()
  }

  // ── colapsado + métrica por sección ──────────────────────────────────────
  toggleSection(key: string): void {
    if (this.expandedSections.has(key)) this.expandedSections.delete(key)
    else this.expandedSections.add(key)
  }
  isSectionCollapsed(key: string): boolean {
    return !this.expandedSections.has(key)
  }
  /**
   * Métrica de la sección para el tab activo: subidos / total + porcentaje + pendientes de revisión.
   *
   * Una sección de puro opcional —«Otros»— no tiene meta que cumplir: se informa cuántos archivos
   * hay, no «0 de 1» con el anillo en rojo, que leería como un pendiente que nadie debe.
   */
  sectionStats(entries: CatalogEntry[]): { filled: number; total: number; pct: number; pendingCount: number; optionalOnly: boolean; fileCount: number } {
    let filled = 0
    let pendingCount = 0
    let fileCount = 0
    for (const e of entries) {
      const v = this.container?.[e.field]
      if (this.isFilled(v)) filled++
      pendingCount += this.countPendingInValue(v)
      fileCount += Array.isArray(v) ? v.filter((f) => !!f?.url).length : v?.url ? 1 : 0
    }
    const total = entries.length
    return { filled, total, pct: total ? Math.round((filled / total) * 100) : 0, pendingCount, optionalOnly: entries.every((e) => e.optional), fileCount }
  }

  // ── lectura ────────────────────────────────────────────────────────────────
  getSingle(field: string): DocumentFile {
    const v = this.container?.[field]
    return v && !Array.isArray(v) ? v : EMPTY_FILE
  }
  getArray(field: string): DocumentFile[] {
    const v = this.container?.[field]
    return Array.isArray(v) ? v : []
  }
  getInterval(field: string): RefreshInterval {
    return this.getSingle(field).refreshInterval
  }
  /** Procedencia: folio del lease de origen, solo en el expediente del cliente. */
  provenanceOf(file: DocumentFile): string | null {
    return this.isClientContext && file?.sourceLeaseFolio ? file.sourceLeaseFolio : null
  }
  /** Cuándo y quién copió el archivo al arrendamiento. Las copias legacy no traen `copiedAt`. */
  copyNote(file: DocumentFile): string {
    if (!file?.copiedAt) return 'Copiado del expediente del cliente'
    const date = formatDate(file.copiedAt, 'dd/MM/yyyy', this.locale)
    return file.copiedByName?.trim() ? `Copiado el ${date} por ${file.copiedByName.trim()}` : `Copiado el ${date}`
  }
  /**
   * Nombre legible del archivo a partir de su url.
   *
   * Se le quita el prefijo `<epoch>-<8 hex>-` que ADR-0033 antepone al blob para que dos
   * archivos con el mismo nombre no colisionen en Azure. Ese token es un detalle de
   * almacenamiento: cuando se filtra a la pantalla, el documento deja de parecer suyo
   * (`1787852517430-b77ea963-IDENTIFICACION.pdf`) y, si además el mes del archivo del banco
   * no es el del periodo que cubre, el usuario concluye que abrió el documento equivocado.
   */
  fileName(url: string): string {
    return nombreLegibleDeUrl(url)
  }
  /** Nombre a mostrar: el original de la subida, o el del blob, o un genérico. */
  displayName(f: DocumentFile): string {
    return f?.name?.trim() || this.fileName(f?.url) || 'Documento'
  }
  fileIcon(url: string): string {
    const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase()
    if (ext === 'pdf') return 'fa-file-pdf'
    if (['doc', 'docx'].includes(ext)) return 'fa-file-word'
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'fa-file-excel'
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'fa-file-image'
    return 'fa-file'
  }

  /** Fecha límite derivada (uploadedAt + intervalo) + si está vencida, para un DocumentFile. */
  dueInfoFile(f: DocumentFile | undefined): { date: Date; overdue: boolean } | null {
    if (!f?.url || !f.refreshInterval || !f.uploadedAt || !INTERVAL_MONTHS[f.refreshInterval]) return null
    const date = new Date(f.uploadedAt)
    date.setMonth(date.getMonth() + INTERVAL_MONTHS[f.refreshInterval])
    return { date, overdue: date.getTime() < Date.now() }
  }
  /** Variante por field (bloque single, inerte con catálogo all-array). */
  dueInfo(field: string): { date: Date; overdue: boolean } | null {
    return this.dueInfoFile(this.getSingle(field))
  }

  // ── revisión (flujo "Por Aprobar") ────────────────────────────────────────
  /** Estado de revisión efectivo: undefined = legacy → 'approved'. */
  reviewStatusOf(f: DocumentFile | undefined): ReviewStatus {
    return f?.reviewStatus ?? 'approved'
  }
  isPending(f: DocumentFile | undefined): boolean {
    return !!f?.url && this.reviewStatusOf(f) === 'pending'
  }
  isRejected(f: DocumentFile | undefined): boolean {
    return !!f?.url && this.reviewStatusOf(f) === 'rejected'
  }
  /** Acciones aprobar/rechazar: solo staff con permiso, SOLO en contexto cliente. */
  canReviewFile(f: DocumentFile | undefined): boolean {
    return this.isClientContext && this.canApprove && (this.isPending(f) || this.isRejected(f))
  }
  /**
   * «Aprobó Arnold Legal», para un archivo aprobado que alguien revisó. Sin revisor (aprobado al subirlo el
   * staff, o anterior al flujo de revisión) no se dice nada: inventar un nombre sería peor.
   */
  approvedByLabel(f: DocumentFile | undefined): string | null {
    const name = f?.reviewedByName?.trim()
    return f?.url && this.reviewStatusOf(f) === 'approved' && name ? `Aprobó ${name}` : null
  }
  approvedByTitle(f: DocumentFile | undefined): string {
    const date = f?.reviewedAt ? new Date(f.reviewedAt) : null
    const label = this.approvedByLabel(f) ?? ''
    return date && !isNaN(date.getTime()) ? `${label} el ${date.toLocaleDateString('es-MX')}` : label
  }
  /** Tooltip de procedencia de la subida ("Subido por Broker", etc.). */
  uploadedByTooltip(f: DocumentFile | undefined): string | null {
    return f?.uploadedByRole ? `Subido por ${f.uploadedByRole}` : null
  }

  private countPendingInValue(v: DocumentFile | DocumentFile[] | undefined): number {
    if (!v) return 0
    if (Array.isArray(v)) return v.filter((f) => this.isPending(f)).length
    return this.isPending(v) ? 1 : 0
  }
  private countPendingIn(container: Record<string, DocumentFile | DocumentFile[]> | undefined | null): number {
    let n = 0
    for (const v of Object.values(container || {})) n += this.countPendingInValue(v)
    return n
  }
  /** Pendientes por aprobar del titular. */
  get holderPendingCount(): number {
    return this.countPendingIn(this.docs?.holder)
  }
  guarantorPendingCount(g: GuarantorDocs): number {
    return this.countPendingIn(g.docs)
  }
  /** Pendientes por aprobar de todo el expediente (titular + avales). */
  get totalPendingCount(): number {
    let n = this.holderPendingCount
    for (const g of this.guarantors) n += this.guarantorPendingCount(g)
    return n
  }

  // ── completitud (excluye archivos rechazados; los pendientes SÍ cuentan) ───
  private isFilled(v: DocumentFile | DocumentFile[] | undefined): boolean {
    if (!v) return false
    return Array.isArray(v) ? v.some((f) => !!f?.url && !this.isRejected(f)) : !!v.url && !this.isRejected(v)
  }
  /** Lo que el expediente PIDE: el cajón «Otros» viene marcado `optional` y no cuenta. */
  private required(entries: CatalogEntry[] | undefined): CatalogEntry[] {
    return (entries || []).filter((e) => !e.optional)
  }
  /**
   * La línea bajo la elección de tipo al agregar un aval (F-043): cuántos documentos pide el catálogo a
   * cada tipo. Antes estaba escrita a mano («6 · 15») y el catálogo pide 7 y 19. Sin catálogo, nada.
   */
  get guarantorRequestHint(): string | null {
    if (!this.catalog) return null
    return `PF solicita ${this.required(this.catalog.guarantor.PF).length} documentos · PM solicita ${this.required(this.catalog.guarantor.PM).length}.`
  }
  get totalDocs(): number {
    if (!this.catalog || !this.docs) return 0
    let total = this.required(this.catalog.holder[this.holderType]).length
    for (const g of this.guarantors) total += this.required(this.catalog.guarantor[g.type]).length
    return total
  }
  get uploadedDocs(): number {
    if (!this.catalog || !this.docs) return 0
    let n = 0
    for (const e of this.required(this.catalog.holder[this.holderType])) if (this.isFilled(this.docs.holder?.[e.field])) n++
    for (const g of this.guarantors) for (const e of this.required(this.catalog.guarantor[g.type])) if (this.isFilled(g.docs?.[e.field])) n++
    return n
  }
  get completeness(): number {
    const t = this.totalDocs
    return t ? Math.round((this.uploadedDocs / t) * 100) : 0
  }

  // ── subida ────────────────────────────────────────────────────────────────
  triggerUpload(field: string, isArray: boolean): void {
    if (!this.isClientContext) return
    this.pendingUpload = { field, isArray }
    this.multiAccept = isArray
    setTimeout(() => this.fileInput?.nativeElement.click())
  }

  onFilePicked(event: Event): void {
    if (!this.isClientContext) return
    const input = event.target as HTMLInputElement
    const files = input.files ? Array.from(input.files) : []
    input.value = ''
    if (!files.length || !this.pendingUpload) return
    const { field, isArray } = this.pendingUpload
    this.pendingUpload = null

    if (files.some((f) => f.size > MAX_FILE_BYTES)) {
      this.toast('error', 'Archivo muy grande', 'El tamaño máximo permitido es 100MB.')
      return
    }

    // La documentación del cliente solo admite PDF (el atributo `accept` es
    // bypasseable seleccionando "todos los archivos", así que validamos aquí también).
    const isPdf = (f: File) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    if (files.some((f) => !isPdf(f))) {
      this.toast('error', 'Formato no permitido', 'Solo se permiten archivos PDF.')
      return
    }

    // Staff: preguntar la vigencia ANTES de subir, para mandarla en el mismo POST
    // (así cada archivo conserva su vigencia).
    if (this.canEditInterval) {
      this.pendingFiles = files
      this.pendingField = field
      this.pendingIsArray = isArray
      this.pendingOldUrl = isArray ? undefined : this.getSingle(field).url
      this.openIntervalPrompt(field)
    } else if (isArray) {
      files.forEach((f) => this.doArrayUpload(field, f, null))
    } else {
      this.doSingleUpload(field, files[0], null, this.getSingle(field).url)
    }
  }

  private doSingleUpload(field: string, file: File, interval: RefreshInterval, oldUrl?: string): void {
    this.uploadingFields.add(field)
    this.postDocument(file, field, false, interval).subscribe((ok) => {
      this.uploadingFields.delete(field)
      if (!ok) return this.toast('error', 'Error', 'No se pudo subir el archivo.')
      this.notifyChanged()
      // Reemplazo: limpiar el blob viejo solo si quedó huérfano (orphan-only).
      if (oldUrl) this.deleteBlobSilent(oldUrl)
    })
  }

  private doArrayUpload(field: string, file: File, interval: RefreshInterval): void {
    this.uploadingFields.add(field)
    this.postDocument(file, field, true, interval).subscribe((ok) => {
      this.uploadingFields.delete(field)
      if (!ok) return this.toast('error', 'Error', 'No se pudo subir el archivo.')
      this.notifyChanged()
    })
  }

  /** Sube + persiste un documento vía POST /documents (field-level) en `client.docs`. */
  private postDocument(file: File, field: string, isArray: boolean, interval: RefreshInterval) {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('id', this.rfc)
    fd.append('documentName', field)
    fd.append('folderName', this.activeTab === 'holder' ? 'holder' : 'guarantors')
    fd.append('docsId', this.docsId)
    fd.append('documentType', this.documentType)
    if (this.activeTab !== 'holder') fd.append('guarantorId', this.activeTab)
    if (interval) fd.append('refreshInterval', interval)
    return this.http.post<any>(`${environment.apiUrl}documents`, fd).pipe(
      map(() => true),
      catchError(() => of(false)),
    )
  }

  // ── persistencia (PUT read-modify-write) ──────────────────────────────────
  private setField(field: string, value: DocumentFile | DocumentFile[]): void {
    if (this.container) this.container[field] = value
  }
  private persist(done?: () => void): void {
    if (!this.docs) return
    this.saving = true
    const payload = this.activeTab === 'holder' ? { holder: this.docs.holder } : { guarantors: this.docs.guarantors }
    this.api.updateDocs(this.docsId, this.documentType, payload).subscribe((r) => {
      this.saving = false
      if (r.error) return this.toast('error', 'Error', r.msg)
      this.notifyChanged()
      if (done) done()
    })
  }

  onIntervalChange(field: string, interval: RefreshInterval): void {
    if (!this.isClientContext) return
    this.setField(field, { ...this.getSingle(field), refreshInterval: interval })
    this.persist()
  }

  /** Cambia la vigencia de un archivo concreto dentro de un campo array (per-entity). */
  onArrayIntervalChange(field: string, index: number, interval: RefreshInterval): void {
    if (!this.isClientContext) return
    const arr = this.getArray(field).map((f, i) => (i === index ? { ...f, refreshInterval: interval } : f))
    this.setField(field, arr)
    this.persist()
  }

  // ── borrado ───────────────────────────────────────────────────────────────
  deleteSingle(field: string): void {
    if (!this.isClientContext) return
    const url = this.getSingle(field).url
    if (!url) return
    this.askConfirm('¿Eliminar este archivo?', () =>
      this.deleteBlob(url, () => {
        this.setField(field, { url: '', uploadedAt: null, refreshInterval: null })
        this.notifyChanged()
      }),
    )
  }
  deleteArrayItem(field: string, url: string): void {
    if (!this.isClientContext) return
    if (!url) return
    this.askConfirm('¿Eliminar este archivo?', () =>
      this.deleteBlob(url, () => {
        this.setField(
          field,
          this.getArray(field).filter((f) => f.url !== url),
        )
        this.notifyChanged()
      }),
    )
  }
  private deleteBlob(url: string, done: () => void): void {
    // Pasamos documentId+documentType para que el backend quite la referencia de ESTA
    // entidad y borre el blob solo si quedó huérfano (no rompe copias de leases).
    const params = `fileUrl=${encodeURIComponent(url)}&documentId=${this.docsId}&documentType=${this.documentType}`
    this.http.delete(`${environment.apiUrl}documents/delete?${params}`).subscribe({
      next: () => done(),
      error: () => this.toast('error', 'Error', 'No se pudo eliminar el archivo.'),
    })
  }

  /** Reemplazo: el doc ya guarda la URL nueva; pedimos borrar el blob viejo solo si
   *  ninguna otra entidad lo referencia (orphan-only, no toca referencias). */
  private deleteBlobSilent(url: string): void {
    this.http.delete(`${environment.apiUrl}documents/blob?fileUrl=${encodeURIComponent(url)}`).subscribe({ next: () => {}, error: () => {} })
  }

  // ── visor / descarga ────────────────────────────────────────────────────────
  /**
   * El visor y la descarga van SIEMPRE contra el `DocumentFile` completo, no
   * contra su url suelta: el nombre con el que se identifica un documento es
   * `displayName()` —lo que se lee en la fila— mientras que la url solo contiene
   * la ruta del blob, que para los archivos subidos desde ADR-0033 lleva un
   * prefijo interno (`<ts>-<uuid8>-`) y para los antiguos puede repetirse entre
   * dos documentos distintos del mismo expediente. Nombrar por la url hacía que
   * dos documentos distintos aterrizaran con el mismo nombre en Descargas y que
   * el usuario abriera el anterior.
   */
  openPreview(file: DocumentFile): void {
    this.previewFile = file
    this.previewUrl = file?.url ?? null
    this.previewName = this.displayName(file)
    this.previewVisible = true
  }
  /** Descarga desde el botón del visor: el archivo es el que el visor tiene abierto. */
  downloadPreview(): void {
    if (this.previewFile) this.download(this.previewFile)
  }
  download(file: DocumentFile): void {
    const url = file?.url
    if (!url) return
    const enc = encodeURIComponent(url)
    const nombre = conExtensionDe(this.displayName(file), url)
    this.http.get(`${environment.apiUrl}documents/download?fileUrl=${enc}`, { responseType: 'blob' }).subscribe({
      next: (blob) => guardarBlobComo(blob, nombre),
      error: () => this.toast('error', 'Error', 'No se pudo descargar el archivo.'),
    })
  }

  // ── prompt de intervalo ─────────────────────────────────────────────────────
  openIntervalPrompt(field: string): void {
    if (!this.isClientContext) return
    this.intervalPromptField = field
    this.showIntervalPrompt = true
  }
  setIntervalFromPrompt(value: RefreshInterval): void {
    if (!this.isClientContext) return
    this.showIntervalPrompt = false
    if (this.pendingFiles && this.pendingFiles.length) {
      // Flujo de subida: subir con la vigencia elegida (un POST por archivo → cada uno
      // conserva su vigencia).
      const files = this.pendingFiles
      const field = this.pendingField
      const isArray = this.pendingIsArray
      const oldUrl = this.pendingOldUrl
      this.pendingFiles = null
      this.pendingField = ''
      this.pendingIsArray = false
      this.pendingOldUrl = undefined
      if (isArray) files.forEach((f) => this.doArrayUpload(field, f, value))
      else this.doSingleUpload(field, files[0], value, oldUrl)
    } else {
      // Cambio de vigencia sin re-subir (per-entity, bloque single inerte).
      this.onIntervalChange(this.intervalPromptField, value)
    }
  }

  // ── aprobar / rechazar (contexto cliente, permiso documents.approve) ──────
  /** Ubicación del archivo dentro del expediente para el payload de review/rename. */
  private fileLocation(field: string, index: number, url: string) {
    return {
      documentType: this.documentType,
      folderName: (this.activeTab === 'holder' ? 'holder' : 'guarantors') as 'holder' | 'guarantors',
      ...(this.activeTab !== 'holder' ? { guarantorId: this.activeTab } : {}),
      field,
      index,
      url,
    }
  }

  approveFile(field: string, index: number, url: string): void {
    if (!this.isClientContext) return
    if (this.reviewBusy) return
    this.reviewBusy = true
    this.api.reviewDocument(this.docsId, { ...this.fileLocation(field, index, url), decision: 'approved' }).subscribe((r) => {
      this.reviewBusy = false
      if (this.handleReviewConflict(r)) return
      if (r.error) return this.toast('error', 'Error', r.msg)
      this.toast('success', 'Documento aprobado', 'El archivo pasó a formar parte del expediente oficial.')
      this.notifyChanged()
    })
  }

  openReject(field: string, index: number, url: string): void {
    if (!this.isClientContext) return
    this.reviewTarget = { field, index, url }
    this.rejectReason = ''
    this.showRejectDialog = true
  }
  confirmReject(): void {
    if (!this.isClientContext) return
    const t = this.reviewTarget
    const reason = this.rejectReason.trim()
    if (!t || !reason || this.reviewBusy) return
    this.reviewBusy = true
    this.api.reviewDocument(this.docsId, { ...this.fileLocation(t.field, t.index, t.url), decision: 'rejected', reason }).subscribe((r) => {
      this.reviewBusy = false
      if (this.handleReviewConflict(r)) return
      if (r.error) return this.toast('error', 'Error', r.msg)
      this.showRejectDialog = false
      this.reviewTarget = null
      this.toast('success', 'Documento rechazado', 'Se registró el rechazo con su motivo.')
      this.notifyChanged()
    })
  }

  /** 409: el expediente cambió (índice/url desfasados) → avisar y recargar. */
  private handleReviewConflict(r: { conflict?: boolean }): boolean {
    if (!r.conflict) return false
    this.showRejectDialog = false
    this.showRenameDialog = false
    this.reviewTarget = null
    this.renameTarget = null
    this.toast('warn', 'Expediente actualizado', 'El expediente cambió, recargando…')
    this.notifyChanged()
    return true
  }

  // ── renombrar archivo (staff, documents.update) ───────────────────────────
  openRename(field: string, index: number, file: DocumentFile): void {
    if (!this.isClientContext) return
    this.renameTarget = { field, index, url: file.url }
    this.renameValue = this.displayName(file)
    this.showRenameDialog = true
  }
  confirmRename(): void {
    if (!this.isClientContext) return
    const t = this.renameTarget
    const name = this.renameValue.trim()
    if (!t || !name || this.saving) return
    this.saving = true
    this.api.renameDocumentFile(this.docsId, { ...this.fileLocation(t.field, t.index, t.url), name }).subscribe((r) => {
      this.saving = false
      if (this.handleReviewConflict(r)) return
      if (r.error) return this.toast('error', 'Error', r.msg)
      this.showRenameDialog = false
      this.renameTarget = null
      this.toast('success', 'Archivo renombrado', 'Se actualizó el nombre del archivo.')
      this.notifyChanged()
    })
  }

  // ── avales ────────────────────────────────────────────────────────────────
  openAddGuarantor(): void {
    if (!this.isClientContext) return
    this.newGuarantorType = 'PF'
    this.newGuarantorName = ''
    this.showAddGuarantor = true
  }
  confirmAddGuarantor(): void {
    if (!this.isClientContext) return
    if (!this.docsId) return
    this.saving = true
    this.api.addGuarantor(this.docsId, this.documentType, this.newGuarantorType, this.newGuarantorName).subscribe((r) => {
      this.saving = false
      this.showAddGuarantor = false
      if (r.error) return this.toast('error', 'Error', r.msg)
      const newId = r.data?._id
      this.toast('success', 'Aval agregado', 'Se agregó el aval/obligado solidario.')
      this.notifyChanged()
      if (newId) this.activeTab = newId
    })
  }
  removeGuarantor(g: GuarantorDocs): void {
    if (!this.isClientContext) return
    if (!this.docsId) return
    this.askConfirm('¿Eliminar este aval y sus archivos?', () => {
      this.saving = true
      this.api.removeGuarantor(this.docsId, this.documentType, g._id).subscribe((r) => {
        this.saving = false
        if (r.error) return this.toast('error', 'Error', r.msg)
        if (this.activeTab === g._id) this.activeTab = 'holder'
        this.toast('success', 'Aval eliminado', 'Se eliminó el aval y sus archivos.')
        this.notifyChanged()
      })
    })
  }

  // ── confirmación ────────────────────────────────────────────────────────────
  askConfirm(message: string, action: () => void): void {
    this.confirmMessage = message
    this.confirmAction = action
    this.showConfirm = true
  }
  doConfirm(): void {
    this.showConfirm = false
    const action = this.confirmAction
    this.confirmAction = null
    if (action) action()
  }
  guarantorLabel(g: GuarantorDocs, idx: number): string {
    return g.name?.trim() ? g.name : `Aval ${idx + 1}`
  }

  private toast(severity: string, summary: string, detail: string): void {
    this.messageService.add({ severity, summary, detail, life: 3500 })
  }
}
